import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  Photo,
  UpdatePhotoInput,
  CreatePhotoTagInput,
  PresignedDownloadUrlResponse,
} from '@peles/shared';
import * as photosApi from '../api/photos';

export function usePhotos(filters?: {
  entityType?: string;
  entityId?: string;
  caption?: string;
  /** 'public' | 'landing' | 'private'. Narrowed in the query, not the browser. */
  publishStatus?: string;
  page?: number;
  limit?: number;
}) {
  return useQuery<{ photos: Photo[]; total: number }>({
    queryKey: ['photos', filters],
    queryFn: () => photosApi.listPhotos(filters),
  });
}

export function usePhoto(id: string) {
  return useQuery<Photo>({
    queryKey: ['photos', id],
    queryFn: () => photosApi.getPhoto(id),
    enabled: !!id,
    refetchInterval: (query) => {
      const status = query.state.data?.transcodeStatus;
      return status === 'PENDING' || status === 'PROCESSING' ? 5000 : false;
    },
  });
}

export function usePhotoDownloadUrl(id: string) {
  return useQuery<PresignedDownloadUrlResponse>({
    queryKey: ['photos', id, 'url'],
    queryFn: () => photosApi.getPresignedDownloadUrl(id),
    enabled: !!id,
    staleTime: 30 * 60 * 1000,
  });
}

export function usePhotoBatchUrls(ids: string[]) {
  return useQuery<Record<string, string>>({
    queryKey: ['photos', 'batch-urls', ids],
    queryFn: () => photosApi.getBatchPhotoUrls(ids),
    enabled: ids.length > 0,
    staleTime: 45 * 60 * 1000,
  });
}

export function useUploadPhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (args: { file: File; caption?: string; tags?: CreatePhotoTagInput[] }) => {
      const { uploadUrl, s3Key } = await photosApi.getPresignedUploadUrl({
        fileName: args.file.name,
        mimeType: args.file.type,
      });
      await photosApi.uploadFileToS3(uploadUrl, args.file);
      const photo = await photosApi.createPhoto({
        s3Key,
        fileName: args.file.name,
        mimeType: args.file.type,
        fileSizeBytes: args.file.size,
        caption: args.caption,
        tags: args.tags,
      });
      return photo;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['photos'] });
    },
  });
}

export function useUpdatePhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdatePhotoInput }) =>
      photosApi.updatePhoto(id, updates),
    onSuccess: (updated) => {
      queryClient.setQueryData(['photos', updated.id], updated);
      queryClient.invalidateQueries({ queryKey: ['photos'] });
    },
  });
}

export function useDeletePhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => photosApi.deletePhoto(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['photos'] });
    },
  });
}

export function useAddPhotoTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ photoId, tag }: { photoId: string; tag: CreatePhotoTagInput }) =>
      photosApi.addPhotoTag(photoId, tag),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['photos', variables.photoId] });
      queryClient.invalidateQueries({ queryKey: ['photos'] });
    },
  });
}

export function useRemovePhotoTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ photoId, tagId }: { photoId: string; tagId: string }) =>
      photosApi.removePhotoTag(photoId, tagId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['photos', variables.photoId] });
      queryClient.invalidateQueries({ queryKey: ['photos'] });
    },
  });
}
